// ScenarioDiffView — v2 full-screen scenario diff: pick A and B, see where
// the journeys share a walk and where they diverge. A diff compares
// JOURNEYS, not verdicts — each side's verdict chip comes from its actual
// suite row (or is absent), never from the comparison.
//
// Data logic is unchanged from the modal era: seeds resolve from the Redux
// cache → RemoteScenarioLoader cache → HTTP fetch (example source).

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectFrames } from '../../store/slices/topologySlice.ts';
import type { RootState } from '../../store/index.ts';
import {
  selectManifest,
  selectActiveSource,
  selectActiveSourceState,
} from '../../store/slices/scenarioSourceSlice.ts';
import { selectSuiteResults } from '../../store/slices/simulationSlice.ts';
import { setAppView } from '../../store/slices/uiSlice.ts';
import { diffScenarios } from '../../services/ScenarioDiffService.ts';
import { fetchScenario, getCachedScenario } from '../../services/RemoteScenarioLoader.ts';
import type { SimulationSeed, ManifestEntry } from '../../types/index.ts';
import { classifyScenarioType, KIND_GLYPHS, VERDICT_CHIP_STYLES } from './scenarioBadges.ts';
import { verdictMapFromSuite } from '../v2/ledgerData.ts';

function SidePanel({
  side,
  entry,
  seed,
  sharedLen,
  nodeLabel,
  verdictChipFor,
}: {
  side: 'A' | 'B';
  entry: ManifestEntry | null;
  seed: SimulationSeed | null;
  sharedLen: number;
  nodeLabel: (id: string) => string;
  verdictChipFor: (scenarioId: string) => { label: string; color: string; bg: string } | null;
}) {
  const accent = side === 'A' ? 'var(--indigo)' : '#a78bfa';
  const kind = entry ? classifyScenarioType(entry) : null;
  const chip = entry ? verdictChipFor(entry.scenarioId) : null;
  const tail = seed ? seed.expectedPath.slice(sharedLen, sharedLen + 10) : [];
  const rest = seed ? seed.expectedPath.length - sharedLen - tail.length : 0;
  return (
    <div className="fv2-diff-card" style={{ borderColor: accent }}>
      <div className="fv2-diff-card-head">
        <span className="fv2-diff-side" style={{ color: accent }}>
          {side} ·{' '}
        </span>
        <span className="fv2-diff-card-label">{entry?.scenarioLabel ?? 'select a scenario'}</span>
        {entry && seed && (
          <span className="fv2-diff-card-meta">
            {kind && `${KIND_GLYPHS[kind]} ${kind.toUpperCase()} · `}
            {seed.expectedPath.length} steps · {seed.events.length} events
            {chip && (
              <>
                {' · '}
                <span style={{ color: chip.color }}>{chip.label}</span>
              </>
            )}
          </span>
        )}
      </div>
      <div className="fv2-diff-card-body">
        {!seed && entry && <div className="fv2-dim">loading…</div>}
        {seed && tail.length === 0 && (
          <div className="fv2-diff-tailnote">
            stops inside the shared prefix — its terminus is a shared step
          </div>
        )}
        {seed &&
          tail.map((nodeId, i) => (
            <div className="fv2-diff-step" key={`${nodeId}-${i}`}>
              <span className="fv2-diff-step-no" style={{ color: accent }}>
                {String(sharedLen + i + 1).padStart(2, '0')}
              </span>
              <span>{nodeLabel(nodeId)}</span>
            </div>
          ))}
        {seed && rest > 0 && (
          <div className="fv2-diff-tailnote">+ {rest} more steps to its terminus</div>
        )}
      </div>
    </div>
  );
}

export default function ScenarioDiffView() {
  const dispatch = useDispatch();
  const frames = useSelector(selectFrames);
  const manifest = useSelector(selectManifest);
  const activeSource = useSelector(selectActiveSource);
  const sourceState = useSelector(selectActiveSourceState);
  const suiteResults = useSelector(selectSuiteResults);
  const scenarioToTopoMap = useSelector(
    (state: RootState) => state.topology.scenarioToTopologyNodeMap,
  );
  const [selectedA, setSelectedA] = useState<string | null>(null);
  const [selectedB, setSelectedB] = useState<string | null>(null);
  const [fetchedA, setFetchedA] = useState<SimulationSeed | null>(null);
  const [fetchedB, setFetchedB] = useState<SimulationSeed | null>(null);

  const scenarioEntries: ManifestEntry[] = useMemo(() => {
    if (!manifest) return [];
    return manifest.flows.flatMap((f) => f.scenarios);
  }, [manifest]);

  const cachedSeedA = selectedA
    ? (sourceState.loadedScenarios[selectedA] ?? getCachedScenario(selectedA))
    : null;
  const cachedSeedB = selectedB
    ? (sourceState.loadedScenarios[selectedB] ?? getCachedScenario(selectedB))
    : null;
  const seedA = cachedSeedA ?? fetchedA;
  const seedB = cachedSeedB ?? fetchedB;

  useEffect(() => {
    if (!selectedA || cachedSeedA || activeSource !== 'example') return;
    const entry = scenarioEntries.find((e) => e.scenarioId === selectedA);
    if (!entry) return;
    let cancelled = false;
    fetchScenario(entry)
      .then((s) => {
        if (!cancelled) setFetchedA(s);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [selectedA, cachedSeedA, scenarioEntries, activeSource]);

  useEffect(() => {
    if (!selectedB || cachedSeedB || activeSource !== 'example') return;
    const entry = scenarioEntries.find((e) => e.scenarioId === selectedB);
    if (!entry) return;
    let cancelled = false;
    fetchScenario(entry)
      .then((s) => {
        if (!cancelled) setFetchedB(s);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [selectedB, cachedSeedB, scenarioEntries, activeSource]);

  const selectA = useCallback((id: string | null) => {
    setSelectedA(id);
    setFetchedA(null);
  }, []);
  const selectB = useCallback((id: string | null) => {
    setSelectedB(id);
    setFetchedB(null);
  }, []);

  const diff = seedA && seedB ? diffScenarios(seedA, seedB) : null;
  const verdicts = verdictMapFromSuite(suiteResults);

  const nodeLabels = useMemo(() => {
    const map = new Map<string, string>();
    for (const frame of frames) for (const node of frame.nodes) map.set(node.id, node.label);
    for (const [scenarioId, topoId] of Object.entries(scenarioToTopoMap)) {
      const label = map.get(topoId);
      if (label) map.set(scenarioId, label);
    }
    return map;
  }, [frames, scenarioToTopoMap]);

  const nodeLabel = useCallback(
    (id: string) => nodeLabels.get(id) ?? id.split('::').slice(-2, -1)[0] ?? id,
    [nodeLabels],
  );

  const entryA = scenarioEntries.find((e) => e.scenarioId === selectedA) ?? null;
  const entryB = scenarioEntries.find((e) => e.scenarioId === selectedB) ?? null;
  const verdictChipFor = useCallback(
    (scenarioId: string) => {
      const v = verdicts[scenarioId] ?? null;
      return v === null ? null : VERDICT_CHIP_STYLES[v];
    },
    [verdicts],
  );

  const optionLabel = (e: ManifestEntry) =>
    `${KIND_GLYPHS[classifyScenarioType(e)]} ${e.scenarioLabel}`;

  const k = diff ? diff.sharedPrefix.length : 0;
  const same = Boolean(selectedA && selectedA === selectedB);

  let summary: string | null = null;
  if (diff) {
    if (same) {
      summary = 'A and B are the same scenario — pick two different ones to compare journeys.';
    } else if (diff.divergencePoints.length === 0) {
      summary = `Both walks are identical for all ${k} shared steps.`;
    } else {
      const d = diff.divergencePoints[0];
      summary =
        `⑂ DIVERGE at step ${d.stepIndex + 1}` +
        (d.lastSharedNode ? ` after “${nodeLabel(d.lastSharedNode)}”` : '') +
        ` — A continues with ${d.nodeA ? `“${nodeLabel(d.nodeA)}”` : 'nothing (A stops)'},` +
        ` B with ${d.nodeB ? `“${nodeLabel(d.nodeB)}”` : 'nothing (B stops)'}.` +
        ` Shared walk: steps 1–${k}.`;
    }
  }

  return (
    <div className="fv2-page" data-screen="diff">
      <div className="fv2-page-inner">
        <div className="fv2-breadcrumb">
          <button className="fv2-crumb-link" onClick={() => dispatch(setAppView('portfolio'))}>
            Ledger
          </button>
          <span className="fv2-crumb-sep">/</span>
          Scenario diff
        </div>
        <h2 className="fv2-flow-title">
          Scenario diff <span className="fv2-flow-title-sub">JOURNEYS, NOT VERDICTS</span>
        </h2>

        <div className="fv2-diff-selectors">
          <span className="fv2-diff-side" style={{ color: 'var(--indigo)' }}>
            A
          </span>
          <select
            className="fv2-select"
            value={selectedA ?? ''}
            onChange={(e) => selectA(e.target.value || null)}
            aria-label="Scenario A"
          >
            <option value="">Select scenario…</option>
            {scenarioEntries.map((s) => (
              <option key={s.scenarioId} value={s.scenarioId}>
                {optionLabel(s)}
              </option>
            ))}
          </select>
          <span className="fv2-diff-side" style={{ color: '#a78bfa' }}>
            B
          </span>
          <select
            className="fv2-select"
            value={selectedB ?? ''}
            onChange={(e) => selectB(e.target.value || null)}
            aria-label="Scenario B"
          >
            <option value="">Select scenario…</option>
            {scenarioEntries.map((s) => (
              <option key={s.scenarioId} value={s.scenarioId}>
                {optionLabel(s)}
              </option>
            ))}
          </select>
        </div>

        {!diff && (
          <div className="fv2-diff-empty">
            Select two scenarios to compare their declared walks.
            {(selectedA && !seedA) || (selectedB && !seedB) ? ' Loading…' : ''}
          </div>
        )}

        {diff && (
          <>
            {summary && <div className="fv2-diff-summary">{summary}</div>}
            {k > 0 && !same && (
              <div className="fv2-diff-shared">
                ≡ steps 1–{k} · <b>identical walk</b> — both scenarios travel the same {k} steps
                {diff.sharedPrefix.length > 0 &&
                  ` — through “${nodeLabel(diff.sharedPrefix[k - 1])}”`}
              </div>
            )}
            {(diff.branchesOnlyA.length > 0 || diff.branchesOnlyB.length > 0) && (
              <div className="fv2-diff-shared">
                ⑂ branch turns — A only: {diff.branchesOnlyA.join(', ') || '—'} · B only:{' '}
                {diff.branchesOnlyB.join(', ') || '—'}
                {diff.sharedBranches.length > 0 && ` · shared: ${diff.sharedBranches.join(', ')}`}
              </div>
            )}
            {!same && (
              <div className="fv2-diff-grid">
                <SidePanel
                  side="A"
                  entry={entryA}
                  seed={seedA}
                  sharedLen={k}
                  nodeLabel={nodeLabel}
                  verdictChipFor={verdictChipFor}
                />
                <SidePanel
                  side="B"
                  entry={entryB}
                  seed={seedB}
                  sharedLen={k}
                  nodeLabel={nodeLabel}
                  verdictChipFor={verdictChipFor}
                />
              </div>
            )}
            <p className="fv2-ledger-note">
              A diff compares journeys, not verdicts — each side&rsquo;s verdict chip is its own
              run&rsquo;s result. Open either scenario from its flow ledger to inspect the evidence
              on the canvas.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
